import React, { ButtonHTMLAttributes, FC, LabelHTMLAttributes } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useResponsiveType } from '../../utils';
import { ThemeTypographyKeys } from '../../styles/tokens/typography';
import { ThemeColorsKeys } from '../../styles/tokens/colors';

type TypographyComponents =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'p'
  | 'span'
  | 'small'
  | 'strong'
  | 'legend'
  | 'label'
  | 'button'
  | 'dt'
  | 'dd'
  | 'li'
  | 'address'
  | 'figcaption'
  | 'div'
  | undefined;

export interface TypographyProps
  extends LabelHTMLAttributes<HTMLLabelElement>,
    ButtonHTMLAttributes<HTMLButtonElement> {
  component?: TypographyComponents;
  variant?: ThemeTypographyKeys;
  color?: ThemeColorsKeys;
  visuallyHidden?: boolean;
  text?: string;
  centered?: boolean;
}

interface TypographyStylesProps {
  component?: TypographyComponents;
  variant: ThemeTypographyKeys;
  color?: ThemeColorsKeys;
  $visuallyHidden?: boolean;
}

const TypographyStyles = styled.p<TypographyStylesProps>`
  ${({ theme: { colors }, variant, color, $visuallyHidden }) => css`
    color: ${color ? colors[color] : 'currentColor'};

    // Variants

    ${variant && useResponsiveType(variant)};

    // Visually hidden

    ${$visuallyHidden &&
    css`
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border-width: 0;
    `}
  `};
`;

export const Typography: FC<React.PropsWithChildren<TypographyProps>> = ({
  children,
  component = 'p',
  variant = 'bodyMedium',
  color,
  visuallyHidden,
  text,
  ...rest
}) => (
  <TypographyStyles
    as={component}
    variant={variant}
    color={color}
    $visuallyHidden={visuallyHidden}
    {...rest}
  >
    {text || children}
  </TypographyStyles>
);

export default Typography;
